/**
 * JudgeCalibration — the judge's agreement-with-humans gauge (UI-SPEC §3.5).
 * The judge (tier-3 signal) is calibrated against human verdicts (tier-1 gold);
 * we track agreement and re-measure on any judge change. Below the target it
 * becomes an optimization target itself. Local-first: surfaces whether the
 * judge is in-house/open vs an external API.
 */

import * as React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../card";
import { Alert, AlertDescription } from "../../alert";
import { IntentBadge } from "../status-badge";
import { formatDateTime } from "../../format";

const TARGET = 0.85; // MT-Bench "good enough to automate" bar.

export interface JudgeCalibrationData {
  judge_model: string;
  agreement: number | null; // 0..1
  updated_at: string;
  /** heuristic: judge model name doesn't look like a frontier API model. */
  in_house: boolean;
}

export function JudgeCalibration({ data }: { data: JudgeCalibrationData | null }) {
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Judge</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="type-body text-muted-foreground">No judge configured for this workflow.</p>
        </CardContent>
      </Card>
    );
  }
  const pct = data.agreement === null ? null : Math.round(data.agreement * 100);
  const below = data.agreement !== null && data.agreement < TARGET;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Judge calibration</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-baseline gap-2">
          <span className="type-heading font-medium tabular-nums text-foreground">
            {pct === null ? "—" : `${pct}%`}
          </span>
          <span className="type-subtext text-muted-foreground">agreement with humans (target {Math.round(TARGET * 100)}%)</span>
        </div>
        {pct !== null && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-selected" role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div
              className={below ? "h-full bg-warning" : "h-full bg-success"}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 type-subtext text-muted-foreground">
          <span className="font-mono">{data.judge_model}</span>
          <IntentBadge intent={data.in_house ? "success" : "neutral"}>
            {data.in_house ? "in-house / open" : "external API"}
          </IntentBadge>
          <span>· measured {formatDateTime(data.updated_at)}</span>
        </div>
        {below && (
          <Alert variant="destructive">
            <AlertDescription>
              Judge drifting below {Math.round(TARGET * 100)}% — pull more human verdicts; the rubric is now an optimization target.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
